import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConsentContext } from '../../context/ConsentProvider/ConsentProvider'
import LotViewToggle from '../../components/LotViewToggle/LotViewToggle.component'
import DisabledMap from '../../components/LotsMap/DisabledMap'
import LotsMap from '../../components/LotsMap/LotsMap'
import PlacesAutocomplete from '../../components/PlacesAutocomplete/PlacesAutocomplete.component'
import useBreakpoint from '../../hooks/useBreakpoint'
import { useFetchLots } from '../../hooks/useFetchLots.hook'
import { MapLocation } from '../../types/mapLocation.interface'
import LotListView from './components/LotListView/LotListView.component'
import {
    Container,
    LotsSectionWrapper,
    SearchContainer,
    Title,
    ToggleIcon,
    ToggleText,
    ViewContainer
} from './Search.style'
import { getLanguageBasedOnDomain } from '@utils/getDomainExtension'
import { SeoHead } from '@components/SeoHead/SeoHead.component'

const Search: FC = () => {
    const { allowGoogleMaps: allowGoogleMapsConsent } = useContext(ConsentContext)
    const [view, setView] = useState<'list' | 'map'>('list')
    const [searchedLocation, setSearchedLocation] = useState<MapLocation | null>(null)
    const { publicLots } = useFetchLots(searchedLocation)
    const [submittedLocation, setSubmittedLocation] = useState<MapLocation | null>(null)
    const [lotMapSelected, setLotMapSelected] = useState<boolean>(false)
    const { t, i18n } = useTranslation()
    const seoLang = i18n.getFixedT(getLanguageBasedOnDomain())

    const breakpoint = useBreakpoint()
    const isMobile = breakpoint <= 1

    const onSearchReset = () => {
        // clear url parameters
        window.history.replaceState({}, '', window.location.pathname)
        setSearchedLocation(null)
        setSubmittedLocation(null)
    }

    const onSearchClick = (loc = searchedLocation) => {
        if (!loc || !loc.lat) {
            onSearchReset()
        } else {
            setSubmittedLocation(loc)
        }
    }

    const onLocationSelect = (place: MapLocation) => {
        if (place && place.lat !== 0 && place.lng !== 0) {
            setSearchedLocation(place)
            onSearchClick(place)
        } else {
            if (place.name === '') {
                onSearchReset()
            }
        }
    }

    useEffect(() => {
        // get url parameters
        const urlParams = new URLSearchParams(window.location.search)
        const lat = urlParams.get('lat')
        const lng = urlParams.get('lng')
        const address = urlParams.get('name')
        if (lat && lng && address) {
            const location: MapLocation = { lat: parseFloat(lat), lng: parseFloat(lng), address, lotId: '' }
            onLocationSelect(location)
            setSubmittedLocation(location)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        // for tablet - expo quick fix
        if (breakpoint === 1) {
            setView('map')
        }
    }, [breakpoint])

    const allowGoogleMaps = allowGoogleMapsConsent || import.meta.env.DEV

    return (
        <>
            <SeoHead title={seoLang('seo.pages.searchTitle')} description={seoLang('seo.pages.searchDescription')} />

            <Container>
                <SearchContainer>
                    <Title>{t('search.title')}</Title>
                    <PlacesAutocomplete onPlaceSelected={onLocationSelect} preselectedLocation={searchedLocation} />
                </SearchContainer>
                <ViewContainer>
                    {view === 'list' ? (
                        <LotsSectionWrapper>
                            <LotListView
                                searchable
                                searchedLocation={submittedLocation}
                                onSearchReset={onSearchReset}
                            />
                        </LotsSectionWrapper>
                    ) : // map view
                    allowGoogleMaps ? (
                        <LotsMap
                            publicLots={publicLots}
                            searchedLocation={submittedLocation}
                            setLotMapSelected={setLotMapSelected}
                            isMobile={isMobile}
                        />
                    ) : (
                        <DisabledMap />
                    )}
                </ViewContainer>

                <LotViewToggle
                    cardShown={view === 'map' && lotMapSelected}
                    onClick={() => setView(view === 'list' ? 'map' : 'list')}
                >
                    <ToggleIcon
                        className={`${view === 'list' ? 'wemolo-map-search' : 'pkd-list'} `}
                        aria-hidden="true"
                    />
                    <ToggleText>{view === 'list' ? t('search.map') : t('search.list')}</ToggleText>
                </LotViewToggle>
            </Container>
        </>
    )
}

export default Search
